/** A realtime channel asked for before the realtime client exists: it records `.on` and `.subscribe` and replays
 * them on the real channel once `loadSupabaseExtras` resolves. Only the chained `.on().subscribe()` form and
 * `removeChannel` are supported — the only form the student's code uses (chat-kit chat-realtime.service.ts). */
export interface RealtimeLike {
    on(...args: unknown[]): RealtimeLike;
    subscribe(callback?: unknown, timeout?: number): RealtimeLike;
}

export interface RealtimeSource {
    channel(name: string, opts?: unknown): RealtimeLike;
    removeChannel(channel: RealtimeLike): Promise<unknown>;
}

export class DeferredChannel {
    private readonly listeners: unknown[][] = [];
    private real: RealtimeLike | null = null;
    private removed = false;
    private callback: unknown = undefined;
    private timeout: number | undefined = undefined;

    constructor(
        private readonly name: string,
        private readonly opts: unknown,
        private readonly source: () => Promise<RealtimeSource>,
    ) {}

    on(...args: unknown[]): this {
        if (this.real) this.real.on(...args);
        else this.listeners.push(args);
        return this;
    }

    subscribe(callback?: unknown, timeout?: number): this {
        this.callback = callback;
        this.timeout = timeout;
        this.attach();
        return this;
    }

    /* realtime-js reconnects by itself when the network returns; a channel whose client never loaded must too. */
    private attach(): void {
        this.source().then(
            (src) => {
                if (this.removed) return;
                const real = src.channel(this.name, this.opts);
                for (const args of this.listeners) real.on(...args);
                this.real = real;
                real.subscribe(this.callback, this.timeout);
            },
            (error: unknown) => {
                console.error('[supabase-lite] realtime did not load', error);
                if (typeof this.callback === 'function') (this.callback as (status: string, err?: unknown) => void)('CHANNEL_ERROR', error);
                if (!this.removed) window.addEventListener('online', () => { if (!this.removed) this.attach(); }, { once: true });
            },
        );
    }

    async detach(): Promise<'ok'> {
        this.removed = true;
        if (!this.real) return 'ok';
        const src = await this.source();
        await src.removeChannel(this.real);
        return 'ok';
    }
}
